import {
  Box,
  Button,
  Flex,
  Icon,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { useRef, useState } from 'react';
import { FiUpload } from 'react-icons/fi';
import { useAuth } from 'contexts/AuthContext';
import { downloadSnapshotTemplate } from 'services/snapshotsService';
import {
  SnapshotTableColumnHeader,
  useSnapshotTableStyles,
} from './snapshotUi';

type SnapshotCsvUploadProps = {
  file: File | null;
  onFileChange: (file: File | null) => void;
  disabled?: boolean;
};

export function SnapshotCsvUpload({
  file,
  onFileChange,
  disabled,
}: SnapshotCsvUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { token } = useAuth() || {};
  const toast = useToast();
  const [dragOver, setDragOver] = useState(false);
  const textColor = useColorModeValue('navy.700', 'white');
  const { borderColor, cellProps } = useSnapshotTableStyles();

  const pickFile = (f: File | undefined) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.csv')) {
      toast({
        title: 'CSV only for now',
        description: 'Please upload a .csv file.',
        status: 'warning',
      });
      return;
    }
    onFileChange(f);
  };

  const handleDownloadTemplate = async () => {
    if (!token) return;
    try {
      const blob = await downloadSnapshotTemplate(token);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'cocorev-snapshot-template.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast({
        title: 'Could not download template',
        status: 'error',
      });
    }
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={2} gap={3} flexWrap="wrap">
        <Text fontSize="xl" fontWeight="bold" color={textColor}>
          Your account list
        </Text>
        <Button size="sm" variant="outline" onClick={handleDownloadTemplate}>
          Download template
        </Button>
      </Flex>
      <Text fontSize="sm" color="gray.500" mb={5} lineHeight="1.55">
        Up to 100 accounts. Use our template, or upload any CSV export and we
        will map the columns.
      </Text>

      <Box
        borderRadius="16px"
        overflow="hidden"
        border="1px solid"
        borderColor={borderColor}
        mb={5}
      >
        <Table size="sm" variant="simple">
          <Thead>
            <Tr>
              <Th borderColor={borderColor} py={3}>
                <SnapshotTableColumnHeader>URL</SnapshotTableColumnHeader>
              </Th>
              <Th borderColor={borderColor} py={3}>
                <SnapshotTableColumnHeader>Company</SnapshotTableColumnHeader>
              </Th>
              <Th borderColor={borderColor} py={3}>
                <SnapshotTableColumnHeader>Lifecycle</SnapshotTableColumnHeader>
              </Th>
              <Th borderColor={borderColor} py={3}>
                <SnapshotTableColumnHeader>Owner</SnapshotTableColumnHeader>
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            <Tr>
              <Td {...cellProps} py={3} fontSize="xs" color="gray.500">
                atlasfield.com
              </Td>
              <Td {...cellProps} py={3} fontSize="xs" color="gray.500">
                Atlasfield Logistics
              </Td>
              <Td {...cellProps} py={3} fontSize="xs" color="gray.500">
                Customer
              </Td>
              <Td {...cellProps} py={3} fontSize="xs" color="gray.500">
                Aoife Byrne
              </Td>
            </Tr>
          </Tbody>
        </Table>
      </Box>

      <Box
        border="2px dashed"
        borderColor={dragOver ? 'brand.400' : borderColor}
        borderRadius="16px"
        bg={file ? 'brand.50' : 'secondaryGray.300'}
        py={12}
        px={4}
        textAlign="center"
        cursor={disabled ? 'not-allowed' : 'pointer'}
        opacity={disabled ? 0.6 : 1}
        transition="border-color 0.15s, background 0.15s"
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (disabled) return;
          pickFile(e.dataTransfer.files?.[0]);
        }}
        onClick={() => !disabled && inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          hidden
          onChange={(e) => pickFile(e.target.files?.[0])}
        />
        <Flex justify="center" mb={3}>
          <Flex
            w="48px"
            h="48px"
            borderRadius="full"
            bg="white"
            align="center"
            justify="center"
            boxShadow="sm"
          >
            <Icon as={FiUpload} boxSize={5} color="gray.600" />
          </Flex>
        </Flex>
        <Text fontWeight="600" color={textColor}>
          {file ? file.name : 'Drop a file or click to choose'}
        </Text>
        <Text fontSize="sm" color="gray.500" mt={1}>
          CSV, up to 100 accounts
        </Text>
      </Box>
    </Box>
  );
}
